import React, { useMemo } from 'react';
import TeamJersey from './TeamJersey';
import QuestionCard from './QuestionCard';
import { OptionPercentageStat } from './OptionPercentageStat';
import TeamLogoBackground from './shared/TeamLogoBackground';
import { createJerseyMap } from '../utils/jersey-resolver';
import { isOrphanCard, ORPHAN_CARD_CLASSES } from './orphan-card';
import { pickFlourishClass, PickSweep, usePickInteraction } from './pick-flourish';

interface PlayerPickProps {
  options: any[];
  selectedTeam?: string;
  onTeamSelect: (teamId: string | undefined) => void;
  points: number;
  earnedPoints?: number;
  questionText?: string;
  subtext?: string;
  isCorrect?: boolean;
  disabled?: boolean;
  className?: string;
  questionId?: string;
  questionNumber?: number;
  questionMetadata?: {
    dailyDouble?: boolean;
  };
}

/**
 * PlayerPick - Component for option selection questions
 * Used when users need to select a option (e.g., "Who will score first goal?")
 * Similar to TeamPick but contextualized for option selection
 */
const PlayerPick: React.FC<PlayerPickProps> = ({
  options,
  selectedTeam,
  onTeamSelect,
  points,
  earnedPoints,
  questionText = 'Select a option',
  subtext,
  isCorrect,
  disabled = false,
  className = '',
  questionId,
  questionNumber,
  questionMetadata,
}) => {
  // Memoize team jersey data to prevent random changes and unnecessary re-renders
  const teamJerseyMap = useMemo(() => createJerseyMap(options), [options]);

  const isDailyDouble = questionMetadata?.dailyDouble || false;
  const { hasInteracted, interactionProps, arm } = usePickInteraction();

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      className={className}
      questionNumber={questionNumber}
      isDailyDouble={isDailyDouble}
      isCorrect={isCorrect}
      data-testid={questionId ? `question-${questionId}` : 'option-pick'}
      data-testtype="option"
    >
      {/* Subtext (if present) */}
      {subtext && (
        <span
          data-testid="option-subtext"
          className="text-[#ABABAB] font-light block text-center mt-2"
        >
          {subtext}
        </span>
      )}

      {/* Question Text */}
      <span
        data-testid="option-question"
        className="text-text-primary font-medium block text-center mb-2 md:mb-4"
      >
        {questionText}
      </span>

      {/* Player Selection */}
      <div
        className={`grid gap-2 md:gap-4 ${options.length > 4 ? 'grid-cols-1' : 'grid-cols-2'}`}
        data-testid="option-selection"
      >
        {options.map((option, index) => {
          const isSelected = selectedTeam === option.id;
          const anySelected = selectedTeam !== undefined;
          const hasResult = isCorrect !== undefined;
          const resultBorderClass =
            isSelected && hasResult
              ? isCorrect
                ? 'border-2 border-green-500 shadow-[0_0_15px_4px_rgba(34,197,94,0.4)]'
                : 'border-2 border-red-500 shadow-[0_0_15px_4px_rgba(239,68,68,0.4)]'
              : '';

          // Single column layout (more than 4 options)
          const isSingleColumn = options.length > 4;
          const isOrphan = isOrphanCard({ index, total: options.length, isSingleColumn });

          // Daily Double styling logic
          const getBackgroundClass = () => {
            if (isSelected) return 'bg-white';
            return isDailyDouble ? 'bg-[#000D1F]' : 'bg-[#121212]';
          };

          const getBorderClass = () => {
            if (hasResult) return resultBorderClass;

            if (isDailyDouble) {
              // Daily Double: blue border default, white on selected
              if (isSelected) return 'border border-white';
              return 'border border-[#0D54AD]';
            }

            // Normal: black border default, gray on selected
            if (isSelected) return 'border border-[#6F6F6F]';
            return 'border border-[#121212]';
          };

          const getHoverClass = () => {
            if (disabled || hasResult) return '';
            // Daily Double: white border on hover
            // Normal: gray border on hover
            return isDailyDouble ? 'hover:border-white' : 'hover:border-[#6F6F6F]';
          };

          return (
            <div
              key={option.id}
              className={`flex flex-col items-center justify-center ${
                isOrphan ? ORPHAN_CARD_CLASSES : ''
              }`}
            >
              <button
                {...interactionProps}
                onClick={() => {
                  arm();
                  if (!disabled) onTeamSelect(isSelected ? undefined : option.id);
                }}
                disabled={disabled}
                data-testid={`option-${option.id}`}
                data-testresult={
                  isSelected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined
                }
                data-selected={isSelected ? 'true' : 'false'}
                className={`
                  group peer w-full h-auto ${isSingleColumn ? 'min-h-[80px]' : 'min-h-[80px] md:min-h-[140px]'} md:p-6 p-2 rounded-xl transition-smooth ${isSingleColumn ? 'grid grid-cols-[auto_1fr_auto] gap-4 items-center' : 'flex flex-col items-center justify-center'} relative overflow-hidden
                  ${getBackgroundClass()}
                  ${getBorderClass()}
                  ${getHoverClass()}
                  ${pickFlourishClass({ isSelected, anySelected, hasResult, disabled, animate: hasInteracted })}
                  ${disabled && !hasResult ? 'cursor-not-allowed opacity-90' : ''}
                `}
              >
                {!isSingleColumn && <TeamLogoBackground logoUrl={option.thirdImageUrl} />}

                {/* Popularity Percentage - Top Left */}
                {option.popularityPct !== undefined && options.length >= 2 && (
                  <div className="absolute top-2 left-2">
                    <OptionPercentageStat percentage={option.popularityPct} isSelected={isSelected} />
                  </div>
                )}

                {isSingleColumn ? (
                  <>
                    {/* Left Column: Jersey (wide enough to not overlap percentage) */}
                    <div className="flex justify-center pl-8">
                      <TeamJersey size="sm" jerseyUrl={teamJerseyMap.get(option.id)} />
                    </div>

                    {/* Middle Column: Player Details */}
                    <div className="flex flex-col justify-center text-left min-w-0">
                      {/* Player Name + Position + Team */}
                      <div className="flex items-center gap-1">
                        <span
                          className={`text-sm font-[600] ${
                            isSelected ? 'text-black' : 'text-white'
                          }`}
                        >
                          {option.name}
                        </span>
                        {option.position && (
                          <span className="text-[#ABABAB] text-sm">{option.position}</span>
                        )}
                        {option.teamShortcode && (
                          <span className="text-[#ABABAB] text-sm">• {option.teamShortcode}</span>
                        )}
                      </div>
                      {/* Fixture Info */}
                      {option.fixture && (
                        <div className="text-xs mt-1">
                          {/* No opponent (a golf round) arrives with blank abbreviations: tee time only. */}
                          {(option.fixture.awayTeamAbbreviation || option.fixture.homeTeamAbbreviation) && (
                          <>
                          <span
                            className={
                              option.isPlayerTeam === 'away' ? isSelected ? 'text-black' : 'text-white' : 'text-[#ABABAB]'
                            }
                          >
                            {option.fixture.awayTeamAbbreviation}
                          </span>
                          <span className="text-[#ABABAB]"> @ </span>
                          <span
                            className={
                              option.isPlayerTeam === 'home' ? isSelected ? 'text-black' : 'text-white' : 'text-[#ABABAB]'
                            }
                          >
                            {option.fixture.homeTeamAbbreviation}
                          </span>
                          </>
                          )}
                          {option.fixture?.matchTime && (
                            <span className="text-[#ABABAB] ml-2">{option.fixture.matchTime}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right Column: Team Logo */}
                    {option.thirdImageUrl && (
                      <div className="flex justify-center">
                        <img
                          src={option.thirdImageUrl}
                          alt="Team Logo"
                          className="w-14 h-14"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className={`relative flex flex-col items-center space-y-1 md:min-h-24 ${
                      // Top margin only clears the popularity badge; without one it is dead
                      // height, and on a phone that height costs a question its place on
                      // screen. Desktop keeps the original spacing either way.
                      option.popularityPct !== undefined && options.length >= 2
                        ? 'mt-4'
                        : 'md:mt-4'
                    }`}
                  >
                    {/* Team Jersey Circle */}
                    <TeamJersey size="sm" sizeMd="md" jerseyUrl={teamJerseyMap.get(option.id)} />

                    {/* Line 1: Small team logo + Player Name (kept compact so long names don't wrap awkwardly with POS/TEAM) */}
                    <div className="z-10 flex justify-center items-center gap-1 px-2">
                      {option.thirdImageUrl && (
                        <img
                          src={option.thirdImageUrl}
                          alt="Player Image"
                          className="w-4 h-4"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <span
                        // Note: don't add an unconditional text-white here —
                        // it generates after text-black in Tailwind's output
                        // and overrides the selected (white-bg) state,
                        // giving white-on-white. The ternary alone is right.
                        className={`text-sm font-[600] whitespace-nowrap ${
                          isSelected ? 'text-black' : 'text-white'
                        }`}
                      >
                        {option.name}
                      </span>
                    </div>

                    {/* Line 2: POS • TEAM on its own line so it never wraps into a stray fragment when the name is long */}
                    {(option.position || option.teamShortcode) && (
                      <div className="text-[#ABABAB] text-xs">
                        {option.position}
                        {option.position && option.teamShortcode && ' • '}
                        {option.teamShortcode}
                      </div>
                    )}

                    {/* Line 3: Away Team @ Home Team — omitted when there is no opponent (a golf round) */}
                    {(option.fixture?.awayTeamAbbreviation || option.fixture?.homeTeamAbbreviation) && (
                      <div className="text-xs text-center">
                        <span
                          className={
                            option.isPlayerTeam === 'away' ? isSelected ? 'text-black' : 'text-white' : 'text-[#ABABAB]'
                          }
                        >
                          {option.fixture.awayTeamAbbreviation}
                        </span>
                        <span className="text-[#ABABAB]"> @ </span>
                        <span
                          className={
                            option.isPlayerTeam === 'home' ? isSelected ? 'text-black' : 'text-white' : 'text-[#ABABAB]'
                          }
                        >
                          {option.fixture.homeTeamAbbreviation}
                        </span>
                      </div>
                    )}

                    {/* Line 4: Match Time */}
                    {option.fixture?.matchTime && (
                      <div className="text-xs text-[#ABABAB]">{option.fixture.matchTime}</div>
                    )}


                  </div>
                )}

                {!hasResult && <PickSweep active={isSelected} animate={hasInteracted} />}
              </button>

              {/* Live Value (shown during live scoring) */}
              {option.liveValue !== undefined && (
              <div
                data-testid={`live-value-${option.id}`}
                className="text-sm font-medium mt-1 text-[#00C853]"
              >
                {option.liveValue}{option.liveStatType ? ` ${option.liveStatType}` : ''}
              </div>
            )}
          </div>
          );

        })}
      </div>
    </QuestionCard>
  );
};

export default PlayerPick;
